<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useEvents } from '@/composables/useEvents'
import { useEditions } from '@/composables/useEditions'
import { useRaces } from '@/composables/useRaces'
import type { Race } from '@/types/events'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import { parseDeletionBlocked, deletionBlockedMessageKey } from '@/utils/deletionBlocked'
import OverflowMenu from '@/components/shared/OverflowMenu.vue'
import HeaderCreateButton from '@/components/shared/HeaderCreateButton.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import { formatCentsAsCurrency } from '@/utils/currencyFormat'

const { t, locale } = useI18n()
const route = useRoute()
const { event, fetchEvent } = useEvents()
const { edition, loading: editionLoading, fetchEdition } = useEditions()
const { races, loading: racesLoading, fetchRaces, deleteRace } = useRaces()

const confirmDeleteRaceId = ref<number | null>(null)
const confirmDeleteRaceName = ref('')
const actionLoading = ref(false)
const successMessage = ref('')
const deleteBlockedMessage = ref<string | null>(null)

const eventSlug = route.params['eventSlug'] as string
const editionSlug = route.params['editionSlug'] as string

onMounted(async () => {
  await Promise.all([fetchEvent(eventSlug), fetchEdition(eventSlug, editionSlug), fetchRaces(eventSlug, editionSlug)])
})

function requestDeleteRace(race: Race): void {
  confirmDeleteRaceId.value = race.id
  confirmDeleteRaceName.value = race.name
  successMessage.value = ''
  deleteBlockedMessage.value = null
}

function cancelDeleteRace(): void {
  deleteBlockedMessage.value = null
  confirmDeleteRaceId.value = null
  confirmDeleteRaceName.value = ''
}

async function confirmDeleteRace(): Promise<void> {
  if (confirmDeleteRaceId.value === null) return
  actionLoading.value = true
  deleteBlockedMessage.value = null
  try {
    const raceToDelete = races.value.find((r) => r.id === confirmDeleteRaceId.value)
    if (!raceToDelete) return
    await deleteRace(eventSlug, editionSlug, raceToDelete.slug)
    successMessage.value = t('races.list.delete_success')
    confirmDeleteRaceId.value = null
    confirmDeleteRaceName.value = ''
  } catch (err: unknown) {
    const blocked = parseDeletionBlocked(err)
    if (blocked === null) throw err
    deleteBlockedMessage.value = t(deletionBlockedMessageKey(blocked.subject))
  } finally {
    actionLoading.value = false
  }
}

function formatRevenue(value: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(Number(value))
}

function formatDistance(value: string): string {
  const n = Number(value)
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, '')
}

function formatStartTime(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}
</script>

<template>
  <OrganizerLayout>

    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- Breadcrumb -->
      <nav aria-label="breadcrumb" class="flex items-center gap-2 text-xs text-slate-500 mb-6 flex-wrap">
        <RouterLink to="/organizer/dashboard" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.dashboard') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink to="/organizer/events" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.events') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink :to="`/organizer/events/${eventSlug}`" class="hover:text-slate-300 transition-colors">
          <template v-if="event">{{ event.name }}</template>
          <span v-else class="text-slate-600">…</span>
        </RouterLink>
        <span aria-hidden="true">/</span>
        <span class="text-slate-300">
          <template v-if="edition">{{ edition.name }}</template>
          <span v-else class="text-slate-600">…</span>
        </span>
      </nav>

      <!-- Page heading -->
      <div class="flex items-start justify-between mb-8 gap-4">
        <div>
          <div class="flex items-center gap-3 flex-wrap">
            <h1 class="text-3xl font-bold text-white">
              {{ event?.name ?? '…' }} · {{ edition?.name ?? '…' }}
            </h1>
            <span
              v-if="edition"
              :class="edition.active
                ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
                : 'bg-slate-700 border-slate-600 text-slate-400'"
              class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium"
            >
              {{ edition.active ? t('editions.list.active_badge') : t('editions.list.inactive_badge') }}
            </span>
          </div>
          <p class="text-slate-400 mt-1">{{ t('editions.detail.races_heading') }}</p>
        </div>

        <div v-if="edition" class="flex items-center gap-2 shrink-0">
          <HeaderCreateButton
            :to="`/organizer/events/${eventSlug}/editions/${editionSlug}/races/create`"
            :label="t('editions.detail.create_race_button')"
            compact
          />
        </div>
      </div>

      <AppSpinner v-if="editionLoading" />

      <template v-else-if="edition">

        <!-- Success message -->
        <div
          v-if="successMessage"
          class="mb-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-5 py-3 text-sm text-emerald-400"
        >
          {{ successMessage }}
        </div>

        <!-- Races loading -->
        <div v-if="racesLoading" class="flex items-center justify-center py-8">
          <svg class="animate-spin h-6 w-6 text-emerald-500" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>

        <!-- Empty state -->
        <div
          v-else-if="races.length === 0"
          class="rounded-xl border border-dashed border-slate-700 px-8 py-12 text-center"
        >
          <p class="text-slate-400 text-sm">{{ t('editions.detail.no_races') }}</p>
        </div>

        <!-- Races list -->
        <div v-else class="space-y-3">
          <article
            v-for="race in races"
            :key="race.id"
            class="rounded-xl bg-slate-800/50 border border-slate-700 px-5 py-4 hover:border-slate-600 transition-colors"
          >
            <div class="flex items-start gap-4">
              <!-- Race info -->
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <RouterLink
                    :to="`/organizer/events/${eventSlug}/editions/${editionSlug}/races/${race.slug}/participants`"
                    class="text-sm font-semibold text-white hover:text-emerald-400 transition-colors"
                  >{{ race.name }}</RouterLink>
                  <span class="shrink-0 inline-flex items-center rounded-full bg-slate-700 border border-slate-600 px-2.5 py-0.5 text-xs font-medium text-slate-300">
                    {{ t(`races.form.type_${race.type}`) }}
                  </span>
                  <span
                    :class="race.registrations_open
                      ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
                      : 'bg-slate-700 border-slate-600 text-slate-400'"
                    class="shrink-0 inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium"
                  >
                    {{ race.registrations_open ? t('races.list.open_badge') : t('races.list.closed_badge') }}
                  </span>
                </div>

                <p class="text-xs text-slate-400 mt-3">
                  {{ formatDistance(race.distance) }} km
                  <span class="mx-1">&middot;</span>
                  {{ formatCentsAsCurrency(race.pricing.current_price_cents, race.pricing.currency, locale) }}
                  <span v-if="race.pricing.current_tier" class="text-slate-500" data-testid="race-tier-name">({{ race.pricing.current_tier.name }})</span>
                  <span class="mx-1">&middot;</span>
                  {{ formatStartTime(race.start_time) }}
                </p>

                <div class="flex items-center gap-3 mt-2">
                  <span class="text-xs text-slate-400">
                    {{ race.participant_count }}{{ race.max_participants !== null ? ` / ${race.max_participants}` : '' }}
                    {{ t('races.list.stat_participants') }}
                    <span class="mx-1">&middot;</span>
                    {{ formatRevenue(race.total_revenue) }} {{ t('races.list.stat_revenue') }}
                  </span>
                </div>
              </div>

              <!-- Actions -->
              <div class="shrink-0 flex items-center gap-2">
                <RouterLink
                  :to="`/organizer/events/${eventSlug}/editions/${editionSlug}/races/${race.slug}/participants`"
                  class="hidden sm:inline-flex text-xs font-medium text-emerald-400 hover:text-emerald-300 transition-colors px-3 py-1.5 rounded-lg border border-emerald-500/30 hover:border-emerald-500/60"
                >
                  {{ t('races.list.action_view') }}
                </RouterLink>
                <RouterLink
                  :to="`/organizer/events/${eventSlug}/editions/${editionSlug}/races/${race.slug}/participants`"
                  class="hidden sm:inline-flex text-xs font-medium text-blue-400 hover:text-blue-300 transition-colors px-3 py-1.5 rounded-lg border border-blue-500/30 hover:border-blue-500/60"
                >
                  {{ t('nav.participants') }}
                </RouterLink>

                <!-- Overflow menu -->
                <OverflowMenu>
                  <!-- Mobile-only: below sm, these detached action buttons are hidden, so
                       they are duplicated here (sm:hidden) to keep the actions reachable. -->
                  <RouterLink
                    :to="`/organizer/events/${eventSlug}/editions/${editionSlug}/races/${race.slug}/participants`"
                    class="sm:hidden w-full text-left px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors flex items-center"
                  >
                    {{ t('races.list.action_view') }}
                  </RouterLink>
                  <RouterLink
                    :to="`/organizer/events/${eventSlug}/editions/${editionSlug}/races/${race.slug}/participants`"
                    class="sm:hidden w-full text-left px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors flex items-center"
                  >
                    {{ t('nav.participants') }}
                  </RouterLink>
                  <hr class="sm:hidden border-slate-700 my-1" />

                  <RouterLink
                    :to="`/organizer/events/${eventSlug}/editions/${editionSlug}/races/${race.slug}/edit`"
                    class="w-full text-left px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors flex items-center"
                  >
                    {{ t('races.list.action_edit') }}
                  </RouterLink>
                  <hr class="border-slate-700 my-1" />
                  <button
                    type="button"
                    class="w-full text-left px-4 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-slate-700/50 transition-colors"
                    @click="requestDeleteRace(race)"
                  >
                    {{ t('races.list.action_delete') }}
                  </button>
                </OverflowMenu>
              </div>
            </div>
          </article>
        </div>
      </template>

      <ConfirmDialog
        :open="confirmDeleteRaceId !== null"
        :title="t('races.list.confirm_delete_title')"
        :body="t('races.list.confirm_delete_body', { name: confirmDeleteRaceName })"
        :confirm-label="t('races.list.confirm_delete_confirm')"
        :loading="actionLoading"
        @update:open="cancelDeleteRace"
        @confirm="confirmDeleteRace"
      >
        <template v-if="deleteBlockedMessage" #warning>
          {{ deleteBlockedMessage }}
        </template>
      </ConfirmDialog>
    </main>
  </OrganizerLayout>
</template>
